import { useRef, useEffect, type ReactNode } from 'react';

interface TypingAreaProps {
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  active: boolean;
  children: ReactNode;
}

/**
 * Invisible focus container that captures all keyboard input.
 * Auto-focuses when active. Never re-renders due to typing state changes.
 */
export default function TypingArea({ onKeyDown, active, children }: TypingAreaProps) {
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (active && divRef.current) {
      divRef.current.focus();
    }
  }, [active]);

  return (
    <div
      ref={divRef}
      className="typing-area"
      tabIndex={0}
      onKeyDown={onKeyDown}
      // Prevent native caret/selection behaviour inside the div
      onCopy={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      role="textbox"
      aria-label="Typing area — start typing to begin"
      aria-multiline="true"
      spellCheck={false}
      autoCorrect="off"
      autoCapitalize="off"
      // Suppress Grammarly and browser grammar overlays
      data-gramm="false"
      data-gramm_editor="false"
      data-enable-grammarly="false"
    >
      {children}
    </div>
  );
}
